import { containsCI, fuzzyScore } from "./match.js";
const RECENCY_HALFLIFE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
/**
 * Decide whether a single QueryPlan matches a hit's "fingerprint" — the
 * combined searchable text. Used recursively for negation evaluation.
 *
 * The fingerprint is just `primary + " " + secondary + " " + matchedFields`.
 * Sources can shape what gets included by what they put in `matchedFields`.
 */
function planMatchesFingerprint(plan, fp) {
    // Exact phrases — every phrase must be present.
    for (const phrase of plan.exactPhrases) {
        if (!containsCI(fp, phrase))
            return false;
    }
    // Predicates — every predicate's value must be present in some form.
    // (The actual field-aware match happens in source.query(); for negation
    // pruning we use a relaxed "any field" check on the value.)
    for (const pred of plan.predicates) {
        if (!containsCI(fp, pred.value))
            return false;
    }
    // Fuzzy terms — at least one must be present (relaxed: ANY fuzzy match
    // counts as "the negation hit something").
    if (plan.fuzzyTerms.length > 0) {
        const anyFuzzy = plan.fuzzyTerms.some((t) => fuzzyScore(t, fp) > 0.4 || containsCI(fp, t));
        if (!anyFuzzy)
            return false;
    }
    // Unions — at least one alternative must match.
    if (plan.unions.length > 0) {
        const anyUnion = plan.unions.some((sub) => planMatchesFingerprint(sub, fp));
        if (!anyUnion)
            return false;
    }
    return true;
}
/**
 * Compute the ranked score for a single hit against a query plan.
 *
 * Returns -Infinity if a hard exclusion applies (negation match).
 * Returns a finite non-negative score otherwise.
 */
export function score(hit, plan, source, now) {
    const fp = [hit.primary, hit.secondary ?? "", ...hit.matchedFields].join(" ");
    // Hard exclusion: any negation matches.
    for (const neg of plan.negations) {
        if (planMatchesFingerprint(neg, fp)) {
            return {
                ...hit,
                score: -Infinity,
                signals: {
                    booleanMatchCount: 0,
                    fuzzyScore: 0,
                    recencyDecay: 0,
                    accessFrequency: 0,
                    sourceWeight: 0,
                    fieldWeightSum: 0,
                },
            };
        }
    }
    // booleanMatchCount: count of predicates + phrases that matched the fingerprint.
    let booleanMatchCount = 0;
    for (const pred of plan.predicates) {
        if (containsCI(fp, pred.value))
            booleanMatchCount++;
    }
    for (const phrase of plan.exactPhrases) {
        if (containsCI(fp, phrase))
            booleanMatchCount++;
    }
    // Unions contribute at most 1 each (the OR-group satisfaction is binary).
    for (const sub of plan.unions) {
        if (planMatchesFingerprint(sub, fp))
            booleanMatchCount++;
    }
    // fuzzyScore: max fuzzy score across all (term × field) pairs, weighted by
    // field weight. This rewards a single strong match more than many weak ones.
    let fuzzyAccum = 0;
    let fieldWeightSum = 0;
    if (plan.fuzzyTerms.length > 0) {
        for (const term of plan.fuzzyTerms) {
            let bestForTerm = 0;
            let bestField = 0;
            for (const fieldName of hit.matchedFields) {
                const fieldDef = source.fields.find((f) => f.name === fieldName);
                if (!fieldDef || !fieldDef.fuzzy)
                    continue;
                const fs = fuzzyScore(term, fp);
                if (fs > bestForTerm) {
                    bestForTerm = fs;
                    bestField = fieldDef.weight;
                }
            }
            fuzzyAccum += bestForTerm;
            fieldWeightSum += bestField;
        }
        // Normalize by term count → 0..1
        fuzzyAccum = Math.min(1, fuzzyAccum / plan.fuzzyTerms.length);
    }
    // recencyDecay — half-life 30 days, exponential, 1.0 at now and approaching 0.
    let recencyDecay = 0;
    if (hit.createdAt) {
        const ageMs = Math.max(0, now.getTime() - hit.createdAt.getTime());
        recencyDecay = Math.exp(-ageMs / RECENCY_HALFLIFE_MS);
    }
    // accessFrequency — 0 in Phase 2-3 (no click-history wired yet).
    // When wired, it's user.clicks(hit.recordId) / max(user.clicks).
    const accessFrequency = hit.accessCount ?? 0;
    const sourceWeight = source.weight;
    const finalScore = booleanMatchCount * 100 +
        fuzzyAccum * 50 +
        recencyDecay * 20 +
        accessFrequency * 15 +
        sourceWeight * 10 +
        fieldWeightSum * 5;
    return {
        ...hit,
        score: finalScore,
        signals: {
            booleanMatchCount,
            fuzzyScore: fuzzyAccum,
            recencyDecay,
            accessFrequency,
            sourceWeight,
            fieldWeightSum,
        },
    };
}
//# sourceMappingURL=score.js.map